import React, { useEffect, useState } from "react";
import {
  Plus, Trash2, FileText, Wallet, Receipt, TrendingDown, Percent, X, LayoutDashboard,
} from "lucide-react";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { useToast } from "../hooks/use-toast";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { ApprovalHistory } from "../components/ApprovalHistory";

const CURRENCIES = ["MYR", "USD", "GBP", "AUD", "CAD", "EUR", "INR", "SGD"];
const STATUS_COLORS = {
  Draft: "bg-slate-100 text-slate-600", Sent: "bg-blue-100 text-blue-700", Paid: "bg-green-100 text-green-700",
  Overdue: "bg-red-100 text-red-700", Cancelled: "bg-slate-100 text-slate-400",
  Pending: "bg-amber-100 text-amber-700", Approved: "bg-cyan-100 text-cyan-700",
};

const Money = ({ map }) => {
  const entries = Object.entries(map || {});
  if (!entries.length) return <span>—</span>;
  return <span>{entries.map(([c, v]) => `${c} ${v.toLocaleString()}`).join(" · ")}</span>;
};

export default function Books() {
  const { user } = useAuth();
  const { toast } = useToast();
  const [tab, setTab] = useState("overview");
  const [countries, setCountries] = useState([]);
  const [summary, setSummary] = useState(null);
  const [invoices, setInvoices] = useState([]);
  const [payments, setPayments] = useState([]);
  const [expenses, setExpenses] = useState([]);
  const [commissions, setCommissions] = useState([]);
  const [invOpen, setInvOpen] = useState(false);
  const [inv, setInv] = useState(null);
  const [simpleOpen, setSimpleOpen] = useState(null); // 'payments'|'expenses'|'commissions'
  const [histId, setHistId] = useState(null);
  const [simpleForm, setSimpleForm] = useState({});

  const defCountry = () => (user?.kind === "super_admin" ? (countries[0]?.id || "") : user?.country_id || "");

  const loadAll = () => {
    api.get("/books/summary").then((r) => setSummary(r.data)).catch(() => {});
    api.get("/books/invoices").then((r) => setInvoices(r.data)).catch(() => {});
    api.get("/books/payments").then((r) => setPayments(r.data)).catch(() => {});
    api.get("/books/expenses").then((r) => setExpenses(r.data)).catch(() => {});
    api.get("/books/commissions").then((r) => setCommissions(r.data)).catch(() => {});
  };
  useEffect(() => {
    api.get("/masters/countries").then((r) => setCountries(r.data)).catch(() => {});
    loadAll();
  }, []);

  // ---- Invoice builder ----
  const newInvoice = () => setInv({ party_name: "", party_email: "", party_type: "Student", country_id: defCountry(), currency: "MYR", items: [{ description: "", qty: 1, unit_price: 0 }], tax_pct: 0, issue_date: "", due_date: "", notes: "", status: "Draft" });
  useEffect(() => { if (invOpen && !inv) newInvoice(); /* eslint-disable-next-line */ }, [invOpen]);

  const invTotals = () => {
    const sub = (inv?.items || []).reduce((s, i) => s + (parseFloat(i.qty) || 0) * (parseFloat(i.unit_price) || 0), 0);
    const tax = sub * (parseFloat(inv?.tax_pct) || 0) / 100;
    return { sub: sub.toFixed(2), tax: tax.toFixed(2), total: (sub + tax).toFixed(2) };
  };

  const saveInvoice = async () => {
    if (!inv.party_name || !inv.country_id) { toast({ title: "Party name & country required", variant: "destructive" }); return; }
    await api.post("/books/invoices", { ...inv, items: inv.items.filter((i) => i.description) });
    toast({ title: "Invoice created" }); setInvOpen(false); setInv(null); loadAll();
  };
  const setInvStatus = async (id, status) => { await api.put(`/books/invoices/${id}`, { status }); toast({ title: `Invoice ${status}` }); loadAll(); };
  const delInvoice = async (id) => { if (!window.confirm("Delete invoice?")) return; await api.delete(`/books/invoices/${id}`); loadAll(); };

  // ---- Simple entities ----
  const openSimple = (kind) => {
    const base = { country_id: defCountry(), currency: "MYR", date: new Date().toISOString().slice(0, 10) };
    if (kind === "payments") setSimpleForm({ ...base, party_name: "", amount: 0, method: "Bank Transfer", reference: "" });
    if (kind === "expenses") setSimpleForm({ ...base, category: "", vendor: "", amount: 0, notes: "" });
    if (kind === "commissions") setSimpleForm({ ...base, agent_name: "", amount: 0, pct: 0, status: "Pending", reference: "" });
    setSimpleOpen(kind);
  };
  const saveSimple = async () => {
    await api.post(`/books/${simpleOpen}`, simpleForm);
    toast({ title: "Saved" }); setSimpleOpen(null); loadAll();
  };
  const delSimple = async (kind, id) => { if (!window.confirm("Delete entry?")) return; await api.delete(`/books/${kind}/${id}`); loadAll(); };
  const approvePayout = async (id) => {
    try {
      const { data } = await api.post(`/books/commissions/${id}/approve`, {});
      if (data?.pending_approval) toast({ title: "Submitted for approval", description: data.message || "Payout awaits approval per the matrix." });
      else toast({ title: "Payout approved" });
      loadAll();
    } catch (e) { toast({ title: "Could not approve", description: e?.response?.data?.detail || "", variant: "destructive" }); }
  };

  const TABS = [
    { k: "overview", l: "Overview", i: LayoutDashboard },
    { k: "invoices", l: "Invoices", i: FileText },
    { k: "payments", l: "Receipts", i: Wallet },
    { k: "expenses", l: "Expenses", i: TrendingDown },
    { k: "commissions", l: "Commissions", i: Percent },
  ];

  return (
    <div>
      <h1 className="font-display text-2xl md:text-3xl font-bold text-[#012a1e]">Emerald Books</h1>
      <p className="text-slate-500 mt-1 text-sm">Country-wise accounts — invoices, receipts, expenses & agent commissions.</p>

      <div className="flex gap-1 bg-white border border-slate-100 rounded-full p-1 mt-5 w-fit overflow-x-auto max-w-full">
        {TABS.map((t) => (
          <button key={t.k} onClick={() => setTab(t.k)} className={`flex items-center gap-2 text-sm font-medium px-4 py-2 rounded-full whitespace-nowrap transition-colors ${tab === t.k ? "bg-[#004030] text-white" : "text-[#004030]"}`}>
            <t.i size={16} /> {t.l}
          </button>
        ))}
      </div>

      {/* OVERVIEW */}
      {tab === "overview" && summary && (
        <>
          <div className="grid grid-cols-2 lg:grid-cols-5 gap-4 mt-6">
            {[
              { label: "Revenue (Paid)", map: summary.revenue, icon: Receipt, color: "#004030" },
              { label: "Outstanding", map: summary.outstanding, icon: FileText, color: "#c9a227" },
              { label: "Expenses", map: summary.expenses, icon: TrendingDown, color: "#b3402f" },
              { label: "Net", map: summary.net, icon: Wallet, color: "#0a5540" },
              { label: "Commissions Due", map: summary.commissions_due, icon: Percent, color: "#013d2b" },
            ].map((c) => (
              <div key={c.label} className="bg-white rounded-2xl border border-slate-100 p-5">
                <div className="w-10 h-10 rounded-xl grid place-items-center text-white" style={{ backgroundColor: c.color }}><c.icon size={18} /></div>
                <p className="font-display text-lg font-bold text-[#012a1e] mt-3"><Money map={c.map} /></p>
                <p className="text-slate-500 text-xs">{c.label}</p>
              </div>
            ))}
          </div>
          <div className="bg-white rounded-2xl border border-slate-100 mt-6">
            <div className="p-5 border-b border-slate-100"><h2 className="font-semibold text-[#012a1e]">Recent Invoices</h2></div>
            <div className="divide-y divide-slate-100">
              {summary.recent_invoices?.length ? summary.recent_invoices.map((i) => (
                <div key={i.id} className="flex items-center justify-between px-5 py-3.5">
                  <div><p className="font-medium text-[#012a1e]">{i.number} · {i.party_name}</p><p className="text-xs text-slate-500">{i.currency} {i.total?.toLocaleString()}</p></div>
                  <span className={`text-xs font-semibold px-3 py-1 rounded-full ${STATUS_COLORS[i.status]}`}>{i.status}</span>
                </div>
              )) : <p className="px-5 py-8 text-center text-slate-400">No invoices yet.</p>}
            </div>
          </div>
        </>
      )}

      {/* INVOICES */}
      {tab === "invoices" && (
        <>
          <div className="flex justify-end mt-5"><Button onClick={() => { setInv(null); setInvOpen(true); }} className="bg-[#004030] hover:bg-[#013d2b] text-white rounded-full px-5"><Plus size={17} className="mr-1" /> New Invoice</Button></div>
          <div className="bg-white rounded-2xl border border-slate-100 mt-4 overflow-x-auto">
            <table className="w-full text-sm">
              <thead><tr className="bg-[#f7faf8] text-left text-slate-500"><th className="px-4 py-3">Number</th><th className="px-4 py-3">Party</th><th className="px-4 py-3">Total</th><th className="px-4 py-3">Status</th><th className="px-4 py-3" /></tr></thead>
              <tbody className="divide-y divide-slate-100">
                {invoices.map((i) => (
                  <tr key={i.id} className="hover:bg-[#f7faf8]">
                    <td className="px-4 py-3 font-medium text-[#012a1e]">{i.number}</td>
                    <td className="px-4 py-3 text-slate-600">{i.party_name}<span className="text-xs text-slate-400 block">{i.party_type}</span></td>
                    <td className="px-4 py-3 text-slate-700">{i.currency} {i.total?.toLocaleString()}</td>
                    <td className="px-4 py-3">
                      <Select value={i.status} onValueChange={(v) => setInvStatus(i.id, v)}>
                        <SelectTrigger className="h-8 w-32 text-xs"><SelectValue /></SelectTrigger>
                        <SelectContent>{["Draft", "Sent", "Paid", "Overdue", "Cancelled"].map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                      </Select>
                    </td>
                    <td className="px-4 py-3 text-right">{user?.kind === "super_admin" && <button onClick={() => delInvoice(i.id)} className="text-slate-400 hover:text-red-600 p-1.5"><Trash2 size={15} /></button>}</td>
                  </tr>
                ))}
                {!invoices.length && <tr><td colSpan={5} className="px-4 py-10 text-center text-slate-400">No invoices yet.</td></tr>}
              </tbody>
            </table>
          </div>
        </>
      )}

      {/* SIMPLE TABLES */}
      {["payments", "expenses", "commissions"].includes(tab) && (
        <>
          <div className="flex justify-end mt-5"><Button onClick={() => openSimple(tab)} className="bg-[#004030] hover:bg-[#013d2b] text-white rounded-full px-5"><Plus size={17} className="mr-1" /> New {tab === "payments" ? "Receipt" : tab.slice(0, -1)}</Button></div>
          <div className="bg-white rounded-2xl border border-slate-100 mt-4 overflow-x-auto">
            <table className="w-full text-sm">
              <thead><tr className="bg-[#f7faf8] text-left text-slate-500">
                {tab === "payments" && <><th className="px-4 py-3">Party</th><th className="px-4 py-3">Method</th></>}
                {tab === "expenses" && <><th className="px-4 py-3">Category</th><th className="px-4 py-3">Vendor</th></>}
                {tab === "commissions" && <><th className="px-4 py-3">Agent</th><th className="px-4 py-3">Status</th></>}
                <th className="px-4 py-3">Amount</th><th className="px-4 py-3">Date</th><th className="px-4 py-3" />
              </tr></thead>
              <tbody className="divide-y divide-slate-100">
                {(tab === "payments" ? payments : tab === "expenses" ? expenses : commissions).map((r) => (
                  <tr key={r.id} className="hover:bg-[#f7faf8]">
                    {tab === "payments" && <><td className="px-4 py-3 text-[#012a1e]">{r.party_name}</td><td className="px-4 py-3 text-slate-600">{r.method}</td></>}
                    {tab === "expenses" && <><td className="px-4 py-3 text-[#012a1e]">{r.category}</td><td className="px-4 py-3 text-slate-600">{r.vendor || "—"}</td></>}
                    {tab === "commissions" && <><td className="px-4 py-3 text-[#012a1e]">{r.agent_name}</td><td className="px-4 py-3"><span className={`text-xs font-semibold px-2.5 py-1 rounded-full ${STATUS_COLORS[r.status]}`}>{r.status}</span></td></>}
                    <td className="px-4 py-3 text-slate-700">{r.currency} {r.amount?.toLocaleString()}</td>
                    <td className="px-4 py-3 text-slate-500">{r.date || "—"}</td>
                    <td className="px-4 py-3 text-right">
                      {tab === "commissions" && r.status !== "Approved" && (
                        <button onClick={() => approvePayout(r.id)} className="text-xs font-semibold text-[#004030] hover:underline mr-3" data-testid={`approve-payout-${r.id}`}>Approve payout</button>
                      )}
                      {tab === "commissions" && (
                        <button onClick={() => setHistId(r.id)} className="text-xs font-semibold text-slate-500 hover:underline mr-3" data-testid={`commission-history-${r.id}`}>History</button>
                      )}
                      <button onClick={() => delSimple(tab, r.id)} className="text-slate-400 hover:text-red-600 p-1.5"><Trash2 size={15} /></button>
                    </td>
                  </tr>
                ))}
                {!(tab === "payments" ? payments : tab === "expenses" ? expenses : commissions).length && <tr><td colSpan={5} className="px-4 py-10 text-center text-slate-400">No records yet.</td></tr>}
              </tbody>
            </table>
          </div>
        </>
      )}

      {/* Invoice dialog */}
      <Dialog open={invOpen} onOpenChange={(o) => { setInvOpen(o); if (!o) setInv(null); }}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-display">New Invoice</DialogTitle></DialogHeader>
          {inv && (
            <div className="space-y-4 py-2">
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-2"><Label>Bill To *</Label><Input value={inv.party_name} onChange={(e) => setInv({ ...inv, party_name: e.target.value })} className="h-11" placeholder="Name" /></div>
                <div className="space-y-2"><Label>Email</Label><Input value={inv.party_email} onChange={(e) => setInv({ ...inv, party_email: e.target.value })} className="h-11" /></div>
              </div>
              <div className="grid grid-cols-3 gap-3">
                <div className="space-y-2"><Label>Type</Label>
                  <Select value={inv.party_type} onValueChange={(v) => setInv({ ...inv, party_type: v })}><SelectTrigger className="h-11"><SelectValue /></SelectTrigger>
                    <SelectContent>{["Student", "Agent", "University", "Other"].map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent></Select>
                </div>
                <div className="space-y-2"><Label>Country</Label>
                  <Select value={inv.country_id} onValueChange={(v) => setInv({ ...inv, country_id: v })}><SelectTrigger className="h-11"><SelectValue placeholder="Select" /></SelectTrigger>
                    <SelectContent>{countries.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent></Select>
                </div>
                <div className="space-y-2"><Label>Currency</Label>
                  <Select value={inv.currency} onValueChange={(v) => setInv({ ...inv, currency: v })}><SelectTrigger className="h-11"><SelectValue /></SelectTrigger>
                    <SelectContent>{CURRENCIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent></Select>
                </div>
              </div>

              <div>
                <Label>Line Items</Label>
                <div className="space-y-2 mt-2">
                  {inv.items.map((it, idx) => (
                    <div key={idx} className="flex gap-2 items-center">
                      <Input value={it.description} onChange={(e) => { const items = [...inv.items]; items[idx].description = e.target.value; setInv({ ...inv, items }); }} placeholder="Description" className="h-10 flex-1" />
                      <Input type="number" value={it.qty} onChange={(e) => { const items = [...inv.items]; items[idx].qty = e.target.value; setInv({ ...inv, items }); }} className="h-10 w-16" />
                      <Input type="number" value={it.unit_price} onChange={(e) => { const items = [...inv.items]; items[idx].unit_price = e.target.value; setInv({ ...inv, items }); }} placeholder="Price" className="h-10 w-24" />
                      <button onClick={() => setInv({ ...inv, items: inv.items.filter((_, i) => i !== idx) })} className="text-slate-400 hover:text-red-600 p-1"><X size={16} /></button>
                    </div>
                  ))}
                </div>
                <button onClick={() => setInv({ ...inv, items: [...inv.items, { description: "", qty: 1, unit_price: 0 }] })} className="text-sm text-[#004030] font-semibold mt-2 flex items-center gap-1"><Plus size={14} /> Add item</button>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-2"><Label>Tax %</Label><Input type="number" value={inv.tax_pct} onChange={(e) => setInv({ ...inv, tax_pct: e.target.value })} className="h-11" /></div>
                <div className="space-y-2"><Label>Due Date</Label><Input type="date" value={inv.due_date} onChange={(e) => setInv({ ...inv, due_date: e.target.value })} className="h-11" /></div>
              </div>

              <div className="bg-[#f7faf8] rounded-xl p-4 text-sm">
                <div className="flex justify-between text-slate-600"><span>Subtotal</span><span>{inv.currency} {invTotals().sub}</span></div>
                <div className="flex justify-between text-slate-600 mt-1"><span>Tax</span><span>{inv.currency} {invTotals().tax}</span></div>
                <div className="flex justify-between font-bold text-[#012a1e] mt-2 pt-2 border-t border-slate-200"><span>Total</span><span>{inv.currency} {invTotals().total}</span></div>
              </div>
            </div>
          )}
          <DialogFooter><Button variant="outline" onClick={() => { setInvOpen(false); setInv(null); }}>Cancel</Button><Button onClick={saveInvoice} className="bg-[#004030] text-white">Create Invoice</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Simple dialog */}
      <Dialog open={!!simpleOpen} onOpenChange={(o) => { if (!o) setSimpleOpen(null); }}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle className="font-display capitalize">New {simpleOpen === "payments" ? "Receipt" : simpleOpen}</DialogTitle></DialogHeader>
          <div className="space-y-4 py-2">
            {simpleOpen === "payments" && <>
              <div className="space-y-2"><Label>Received From *</Label><Input value={simpleForm.party_name || ""} onChange={(e) => setSimpleForm({ ...simpleForm, party_name: e.target.value })} className="h-11" /></div>
              <div className="space-y-2"><Label>Method</Label><Input value={simpleForm.method || ""} onChange={(e) => setSimpleForm({ ...simpleForm, method: e.target.value })} className="h-11" /></div>
            </>}
            {simpleOpen === "expenses" && <>
              <div className="space-y-2"><Label>Category *</Label><Input value={simpleForm.category || ""} onChange={(e) => setSimpleForm({ ...simpleForm, category: e.target.value })} className="h-11" placeholder="e.g. Rent, Marketing" /></div>
              <div className="space-y-2"><Label>Vendor</Label><Input value={simpleForm.vendor || ""} onChange={(e) => setSimpleForm({ ...simpleForm, vendor: e.target.value })} className="h-11" /></div>
            </>}
            {simpleOpen === "commissions" && <>
              <div className="space-y-2"><Label>Agent Name *</Label><Input value={simpleForm.agent_name || ""} onChange={(e) => setSimpleForm({ ...simpleForm, agent_name: e.target.value })} className="h-11" /></div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-2"><Label>Percent %</Label><Input type="number" value={simpleForm.pct || 0} onChange={(e) => setSimpleForm({ ...simpleForm, pct: parseFloat(e.target.value) || 0 })} className="h-11" /></div>
                <div className="space-y-2"><Label>Status</Label>
                  <Select value={simpleForm.status || "Pending"} onValueChange={(v) => setSimpleForm({ ...simpleForm, status: v })}><SelectTrigger className="h-11"><SelectValue /></SelectTrigger>
                    <SelectContent>{["Pending", "Approved", "Paid"].map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent></Select>
                </div>
              </div>
            </>}
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2"><Label>Amount *</Label><Input type="number" value={simpleForm.amount || 0} onChange={(e) => setSimpleForm({ ...simpleForm, amount: parseFloat(e.target.value) || 0 })} className="h-11" /></div>
              <div className="space-y-2"><Label>Currency</Label>
                <Select value={simpleForm.currency || "MYR"} onValueChange={(v) => setSimpleForm({ ...simpleForm, currency: v })}><SelectTrigger className="h-11"><SelectValue /></SelectTrigger>
                  <SelectContent>{CURRENCIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent></Select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2"><Label>Date</Label><Input type="date" value={simpleForm.date || ""} onChange={(e) => setSimpleForm({ ...simpleForm, date: e.target.value })} className="h-11" /></div>
              <div className="space-y-2"><Label>Country</Label>
                <Select value={simpleForm.country_id || ""} onValueChange={(v) => setSimpleForm({ ...simpleForm, country_id: v })}><SelectTrigger className="h-11"><SelectValue placeholder="Select" /></SelectTrigger>
                  <SelectContent>{countries.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent></Select>
              </div>
            </div>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setSimpleOpen(null)}>Cancel</Button><Button onClick={saveSimple} className="bg-[#004030] text-white">Save</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!histId} onOpenChange={(o) => !o && setHistId(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>Payout approval history</DialogTitle></DialogHeader>
          {histId && <ApprovalHistory entityType="commission" entityId={histId} emptyText="This payout has no approval record yet." />}
        </DialogContent>
      </Dialog>
    </div>
  );
}
